import { lazy, Suspense } from 'react'
import {
  createRootRoute,
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import { supabase } from '@/lib'
import {
  requireAuth,
  requireInstructor,
  requireAdmin,
  LoginPage,
  SignupPage,
  ForgotPasswordPage,
  UpdatePasswordPage,
} from '@/features/auth'
import { DashboardRouter } from '@/features/dashboard'
import { HomePage } from '@/features/home'

const LazyEnrollmentRequestsPage = lazy(
  () => import('@/features/dashboard/admin/EnrollmentRequestsPage')
)
const LazyChannelsAdminPage = lazy(
  () => import('@/features/admin/community/ChannelsAdminPage')
)
const LazyEventsAdminPage = lazy(
  () => import('@/features/admin/community/EventsAdminPage')
)
const LazyAnnouncementsAdminPage = lazy(
  () => import('@/features/admin/community/AnnouncementsAdminPage')
)
const LazyClassroomAdminPage = lazy(
  () => import('@/features/admin/community/ClassroomAdminPage')
)
const LazyVideoSubmissionsAdminPage = lazy(
  () => import('@/features/admin/community/VideoSubmissionsAdminPage')
)
const LazyOnboardingResponsesAdminPage = lazy(
  () => import('@/features/admin/community/OnboardingResponsesAdminPage')
)
// Route-level code splitting: each page's code loads when its route is visited.
const ResourcesPage = lazyRouteComponent(() => import('@/features/dashboard/ResourcesPage'))
const InstructorDashboard = lazyRouteComponent(() => import('@/features/dashboard/instructor/InstructorDashboard'))
const StudentDashboard = lazyRouteComponent(() => import('@/features/dashboard/student/StudentDashboard'))
const AdminDashboard = lazyRouteComponent(() => import('@/features/dashboard/admin/AdminDashboard'))
const SessionRoomPage = lazyRouteComponent(() => import('@/features/live-sessions/SessionRoomPage'))
const TrainingListPage = lazyRouteComponent(() => import('@/features/trainings/TrainingListPage'))
const TrainingDetailPage = lazyRouteComponent(() => import('@/features/trainings/TrainingDetailPage'))
const LessonViewerPage = lazyRouteComponent(() => import('@/features/trainings/LessonViewerPage'))
const TrainingManagePage = lazyRouteComponent(() => import('@/features/trainings/TrainingManagePage'))
const TrainingForm = lazyRouteComponent(() => import('@/features/trainings/TrainingForm'))
const ProfilePage = lazyRouteComponent(() => import('@/features/profile/ProfilePage'))
const AboutPage = lazyRouteComponent(() => import('@/features/marketing/AboutPage'))
const CertificationsPage = lazyRouteComponent(() => import('@/features/marketing/CertificationsPage'))
const ContactPage = lazyRouteComponent(() => import('@/features/marketing/ContactPage'))
const PartnersPage = lazyRouteComponent(() => import('@/features/marketing/PartnersPage'))
const TrainingsPage = lazyRouteComponent(() => import('@/features/marketing/TrainingsPage'))
const TestimonialsPage = lazyRouteComponent(() => import('@/features/marketing/TestimonialsPage'))
const ProgrammesDetailPage = lazyRouteComponent(() => import('@/features/marketing/ProgrammesDetailPage'))
const CommunityLandingPage = lazyRouteComponent(() => import('@/features/community/CommunityLandingPage'))
const CommunityFeedPage = lazyRouteComponent(() => import('@/features/community/CommunityFeedPage'))
const CommunityCalendarPage = lazyRouteComponent(() => import('@/features/community/CommunityCalendarPage'))
const CommunityMapPage = lazyRouteComponent(() => import('@/features/community/CommunityMapPage'))
const MembersPage = lazyRouteComponent(() => import('@/features/community/MembersPage'))
const CommunityClassroomPage = lazyRouteComponent(() => import('@/features/community/CommunityClassroomPage'))
const ClassroomCoursePage = lazyRouteComponent(() => import('@/features/community/ClassroomCoursePage'))
const LeaderboardPage = lazyRouteComponent(() => import('@/features/community/LeaderboardPage'))
const EventDetailPage = lazyRouteComponent(() => import('@/features/community/EventDetailPage'))

const rootRoute = createRootRoute({
  beforeLoad: async () => {
    const hash = window.location.hash.slice(1)
    const params = new URLSearchParams(hash)
    if (params.get('type') === 'invite') {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        localStorage.setItem('must_set_password', 'true')
        window.history.replaceState({}, '', window.location.pathname)
        throw redirect({ to: '/dashboard' })
      }
    }
  },
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (session) throw redirect({ to: '/dashboard' })
  },
  component: HomePage,
})

const partnersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partners',
  component: PartnersPage,
})

const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/about',
  component: AboutPage,
})

// Public marketing page. Authenticated training catalog is trainingListRoute at /trainings.
const trainingsMarketingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/programmes',
  component: TrainingsPage,
})

const programmesDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/programmes/$trainingId',
  component: ProgrammesDetailPage,
})

const certificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/certifications',
  component: CertificationsPage,
})

const testimonialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testimonials',
  component: TestimonialsPage,
})

const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contact',
  component: ContactPage,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupPage,
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
})

const updatePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/update-password',
  component: UpdatePasswordPage,
})

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  beforeLoad: requireAuth,
  component: DashboardRouter,
})

const adminDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/admin',
  beforeLoad: requireAdmin,
  component: AdminDashboard,
})

const adminEnrollmentRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/enrollment-requests',
  beforeLoad: requireAdmin,
  component: () => (
    <Suspense fallback={null}>
      <LazyEnrollmentRequestsPage />
    </Suspense>
  ),
})

const adminCommunityChannelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/community/channels',
  beforeLoad: requireAdmin,
  component: () => (
    <Suspense fallback={null}>
      <LazyChannelsAdminPage />
    </Suspense>
  ),
})

const adminCommunityEventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/community/events',
  beforeLoad: requireAdmin,
  component: () => (
    <Suspense fallback={null}>
      <LazyEventsAdminPage />
    </Suspense>
  ),
})

const adminCommunityAnnouncementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/community/announcements',
  beforeLoad: requireAdmin,
  component: () => (
    <Suspense fallback={null}>
      <LazyAnnouncementsAdminPage />
    </Suspense>
  ),
})

const adminCommunityClassroomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/community/classroom',
  beforeLoad: requireAdmin,
  component: () => (
    <Suspense fallback={null}>
      <LazyClassroomAdminPage />
    </Suspense>
  ),
})

const adminCommunityVideosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/community/videos',
  beforeLoad: requireAdmin,
  component: () => (
    <Suspense fallback={null}>
      <LazyVideoSubmissionsAdminPage />
    </Suspense>
  ),
})

const adminCommunityOnboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/community/onboarding',
  beforeLoad: requireAdmin,
  component: () => (
    <Suspense fallback={null}>
      <LazyOnboardingResponsesAdminPage />
    </Suspense>
  ),
})

const instructorDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/instructor',
  beforeLoad: requireAuth,
  component: InstructorDashboard,
})

const studentDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/student',
  beforeLoad: requireAuth,
  component: StudentDashboard,
})

const resourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources',
  beforeLoad: requireAuth,
  component: ResourcesPage,
})

const sessionRoomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/session/$sessionId',
  beforeLoad: requireAuth,
  component: SessionRoomPage,
})

const trainingListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trainings',
  beforeLoad: requireAuth,
  component: TrainingListPage,
})

const trainingDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trainings/$trainingId',
  beforeLoad: requireAuth,
  component: TrainingDetailPage,
})

const lessonViewerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trainings/$trainingId/learn/$lessonId',
  beforeLoad: requireAuth,
  component: LessonViewerPage,
})

const trainingManageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/instructor/trainings',
  beforeLoad: requireInstructor,
  component: TrainingManagePage,
})

const trainingFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/instructor/trainings/$trainingId/edit',
  beforeLoad: requireInstructor,
  component: TrainingForm,
})

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  beforeLoad: requireAuth,
  component: ProfilePage,
})

const communityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community',
  component: CommunityLandingPage,
})

const communityFeedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/feed',
  validateSearch: (search: Record<string, unknown>): { channel?: string } => ({
    channel: typeof search.channel === 'string' ? search.channel : undefined,
  }),
  beforeLoad: requireAuth,
  component: CommunityFeedPage,
})

const communityClassroomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/classroom',
  beforeLoad: requireAuth,
  component: CommunityClassroomPage,
})

const communityCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/calendar',
  beforeLoad: requireAuth,
  component: CommunityCalendarPage,
})

const communityMembersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/members',
  beforeLoad: requireAuth,
  component: MembersPage,
})

const communityMapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/map',
  beforeLoad: requireAuth,
  component: CommunityMapPage,
})

const communityLeaderboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/leaderboard',
  beforeLoad: requireAuth,
  component: LeaderboardPage,
})

const communityEventDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/academy/$eventId',
  beforeLoad: requireAuth,
  component: EventDetailPage,
})

const communityClassroomCourseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/community/classroom/$courseId',
  beforeLoad: requireAuth,
  component: ClassroomCoursePage,
})

export const routeTree = rootRoute.addChildren([
  indexRoute,
  partnersRoute,
  aboutRoute,
  trainingsMarketingRoute,
  programmesDetailRoute,
  certificationsRoute,
  testimonialsRoute,
  contactRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  updatePasswordRoute,
  dashboardRoute,
  adminDashboardRoute,
  adminEnrollmentRequestsRoute,
  adminCommunityChannelsRoute,
  adminCommunityEventsRoute,
  adminCommunityAnnouncementsRoute,
  instructorDashboardRoute,
  studentDashboardRoute,
  resourcesRoute,
  profileRoute,
  sessionRoomRoute,
  trainingListRoute,
  trainingDetailRoute,
  lessonViewerRoute,
  trainingManageRoute,
  trainingFormRoute,
  communityRoute,
  communityFeedRoute,
  communityClassroomRoute,
  communityCalendarRoute,
  communityMembersRoute,
  communityMapRoute,
  communityLeaderboardRoute,
  communityEventDetailRoute,
  communityClassroomCourseRoute,
  adminCommunityClassroomRoute,
  adminCommunityVideosRoute,
  adminCommunityOnboardingRoute,
])